import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";
import {
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import PopupMenu from "components/Elements/PopupMenu";
import ColourConstants from "helpers/colourConstants";
import TableStyle from "styles/application/TableStyle";
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import { handleSort } from "helpers/utils";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import { EDIT_ACCESS, FULL_ACCESS } from "constants/AccessTypes/AccessTypes";

const AT = TableStyle();

const mediaMobile = "@media(max-width: 414px)";
const mediaIpad = "@media(max-width: 1024px)";

const useStyles = makeStyles()((theme) => ({
	tableContainer: {
		tableLayout: "fixed",
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
		[mediaMobile]: {
			tableLayout: "auto",
		},
		[mediaIpad]: {
			maxWidth: "100%",
			overflowX: "auto",
			tableLayout: "auto",
		},
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
		borderRightColor: "#979797",
		borderRightStyle: "solid",
		borderRightWidth: "1px",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: "20%",
	},

	dataCell: {
		height: 50,
	},
	defaultText: {
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		color: ColourConstants.tableBorder,
		fontStyle: "italic",
		paddingLeft: 5,
		display: "inline-flex",
	},
	defaultNameText: {
		fontWeight: "bold",
	},
	tableHead: {
		whiteSpace: "nowrap",
	},
}));

const CommonApplicationTable = ({
	data,
	setData,
	setSearch,
	searchedData,
	searchQuery,
	columns,
	headers,
	isLoading,
	defaultID,
	menuData,
	access,
	handleEdit,
	handleDelete,
	handleArrangementEdit,
	modelArrangements,
}) => {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const [currentTableSort, setCurrentTableSort] = useState(["name", "asc"]);
	const [selectedData, setSelectedData] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

	// Handlers
	const handleSortClick = (field) => {
		// Flipping current method
		const newMethod =
			currentTableSort[0] === field && currentTableSort[1] === "asc"
				? "desc"
				: "asc";

		// Sorting table
		if (searchQuery.length === 0) handleSort(data, setData, field, newMethod);
		else handleSort(searchedData, setSearch, field, newMethod);

		// Updating header state
		setCurrentTableSort([field, newMethod]);
	};

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<AutoFitContentInScreen containsTable>
			<Table aria-label="Table" className={classes.tableContainer}>
				<AT.TableHead>
					<TableRow className={classes.tableHead}>
						{headers.map((header, index) => (
							<TableCell
								key={header}
								onClick={() => {
									handleSortClick(columns[index]);
								}}
								className={cx(classes.nameRow, {
									[classes.selectedTableHeadRow]:
										currentTableSort[0] === columns[index],
									[classes.tableHeadRow]:
										currentTableSort[0] !== columns[index],
								})}
							>
								<AT.CellContainer className="flex justify-between">
									{header}
									{currentTableSort[0] === columns[index] &&
									currentTableSort[1] === "desc" ? (
										<AT.DefaultArrow fill="#FFFFFF" />
									) : (
										<AT.DescArrow fill="#FFFFFF" />
									)}
								</AT.CellContainer>
							</TableCell>
						))}
					</TableRow>
				</AT.TableHead>
				<TableBody>
					{data.length !== 0 ? (
						(searchQuery.length === 0 ? data : searchedData).map(
							(row, index) => (
								<TableRow
									key={row.id}
									id={`asset-${row.id}`}
									data-testid={`model-assets-row-${row.id}`}
									className="assetEl"
								>
									{columns.map((col, i, arr) => (
										<TableCell
											key={col}
											scope="row"
											className={cx(classes.dataCell, classes.nameRow, {
												[classes.lastCell]: index === data.length - 1,
											})}
										>
											<AT.CellContainer key={col}>
												<AT.TableBodyText
													className={cx({
														[classes.defaultNameText]:
															row.id === defaultID && i === 0,
													})}
												>
													{`${row[col] || ""}`}
												</AT.TableBodyText>
												{row.id === defaultID && i === 0 ? (
													<Typography className={classes.defaultText}>
														{t("label.default")}
													</Typography>
												) : null}
												{arr.length === i + 1 ? (
													<AT.DotMenu
														data-testid={`model-assets-row-${row.id}-actions`}
														onClick={(e) => {
															setAnchorEl(
																anchorEl === e.currentTarget
																	? null
																	: e.currentTarget
															);
															setSelectedData(
																anchorEl === e.currentTarget ? null : index
															);
														}}
													>
														{menuData.length > 0 && (
															<AT.TableMenuButton>
																<MenuIcon />
															</AT.TableMenuButton>
														)}

														<PopupMenu
															testIdPrefix={`model-assets-${row.id}`}
															index={index}
															selectedData={selectedData}
															anchorEl={anchorEl}
															id={row.id}
															clickAwayHandler={() => {
																setAnchorEl(null);
																setSelectedData(null);
															}}
															menuData={[
																{
																	name:
																		row["isActive"] === true
																			? t("action.makeInactive")
																			: t("action.makeActive"),
																	handler: handleEdit,
																	isDelete: false,
																},
																//for arrangements modifications -- hide edit if no active arrangements for the modal
																...(modelArrangements?.arragements?.length > 0
																	? [
																			{
																				name: t("title.editEntity", {
																					entity: modelArrangements?.title,
																				}),
																				handler: () => {
																					handleArrangementEdit(row);
																				},
																				isDelete: false,
																			},
																	  ]
																	: []),
																{
																	name: t("action.remove"),
																	handler: handleDelete,
																	isDelete: true,
																},
															].filter((x) => {
																if (access === FULL_ACCESS) return true;
																if (access === EDIT_ACCESS) {
																	if (x.name === "Edit") return true;
																	else return false;
																}
																return false;
															})}
														/>
													</AT.DotMenu>
												) : null}
											</AT.CellContainer>
										</TableCell>
									))}
								</TableRow>
							)
						)
					) : (
						<TableRow>
							{headers.map((head, i) => {
								if (i === 0) {
									return (
										<TableCell key={head}>
											{t("message.noRecordsFound")}
										</TableCell>
									);
								} else {
									return <TableCell key={head}></TableCell>;
								}
							})}
						</TableRow>
					)}
				</TableBody>
			</Table>
		</AutoFitContentInScreen>
	);
};

CommonApplicationTable.defaultProps = {
	data: [
		{
			id: 1,
			name: "XYZ",
		},
		{
			id: 2,
			name: "ABC",
		},
	],
	columns: ["name"],
	headers: ["Name"],
	defaultID: null,
};

CommonApplicationTable.propTypes = {
	data: PropTypes.array,
	defaultID: PropTypes.number,
	columns: PropTypes.array,
	headers: PropTypes.array,
};

export default CommonApplicationTable;
